import React, { useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  hideCustomerWelcomeMessage,
  modalCustomerWelcomeVisibleChanged,
  reRateByNopCommerCustomer,
} from '../../actions';
import syncIcon from '../../assets/sync-clouds-citron.svg';
import { forceFocusElement } from '../../util';
import ModalContainer from './ModalContainer';
import './ModalCustomerWelcome.css';

let isMounted = false;
const ModalCustomerWelcome = ({ goTo }) => {
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const dispatch = useDispatch();
  const {
    data,
    isLoading,
    modalCustomerWelcomeVisible,
    parameters,
    petQuoteSelected,
    quoteSelectedValues,
    sessionInformation,
    subId,
  } = store;
  const { nopCommerceUser, userEmail } = sessionInformation;
  const buttonRef = useRef(null);
  const ESSENTIAL_PLAN = store.plans[0];
  const PREFERRED_PLAN = store.plans[1];
  const ULTIMATE_PLAN = store.plans[2];

  const isNewZipcode = useMemo(
    () => (parameters.petZipCode !== nopCommerceUser.Address?.ZipCode),
    [parameters.petZipCode, nopCommerceUser.Address],
  );

  useEffect(() => {
    if (!isMounted) {
      isMounted = true;
    }

    return () => dispatch(hideCustomerWelcomeMessage());
  }, [dispatch]);

  useEffect(() => {
    if (!isMounted || !nopCommerceUser.LoginSuccess) {
      return;
    }

    if (sessionInformation.showWelcomeMessage || isNewZipcode) {
      dispatch(modalCustomerWelcomeVisibleChanged(true));
    }
  }, [
    dispatch,
    isNewZipcode,
    nopCommerceUser.LoginSuccess,
    sessionInformation,
  ]);

  useEffect(() => {
    if (modalCustomerWelcomeVisible && !isLoading && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [isLoading, modalCustomerWelcomeVisible]);

  function handleCloseFocus() {
    const quoteSelected = quoteSelectedValues
      .find((item) => item.quoteId === petQuoteSelected)
      || { petName: 'Pet Name' };

    switch (quoteSelected.selectedPlanIndex) {
      case ESSENTIAL_PLAN.index:
        forceFocusElement(`ESSENTIAL${ESSENTIAL_PLAN.index}`);
        break;

      case PREFERRED_PLAN.index:
        forceFocusElement(`PREFERRED${PREFERRED_PLAN.index}`);
        break;

      case ULTIMATE_PLAN.index:
        forceFocusElement(`ULTIMATE${ULTIMATE_PLAN.index}`);
        break;

      default:
        forceFocusElement(`PREFERRED${PREFERRED_PLAN.index}`);
        break;
    }
  }

  async function onClickGotIt() {
    const { FirstName, LastName } = nopCommerceUser;
    dispatch(modalCustomerWelcomeVisibleChanged(false));

    await dispatch(reRateByNopCommerCustomer({
      diamondClientId: nopCommerceUser.DiamonClientdId,
      effectiveDateCustom: store.effectiveDateCustom,
      eMail: userEmail || parameters.email,
      employerName: `${FirstName} ${LastName}`,
      nopCommerceClientId: nopCommerceUser.CustomerNopCommerceId,
      parameters,
      petQuoteResponseList: data.petQuoteResponseList,
      promoCode: store.newPet.promoCode,
      subId: subId && subId.Name && subId.Value ? subId : {},
      zipCode: nopCommerceUser.Address.ZipCode,
    }));

    handleCloseFocus();

    if (goTo) {
      goTo();
    }
  }

  function renderContent() {
    if (!isNewZipcode) {
      return (
        <p>
          We’ve pre-filled some information from your
          existing Pet Cloud account.
        </p>
      );
    }

    return (
      <p>
        We’ve updated your quote with the zip code in your Pet Cloud
        ({nopCommerceUser.Address.ZipCode}) which may affect premium and other
        options available with your policy. Just to be sure, please
        review the plans once again.
      </p>
    );
  }

  return (
    <ModalContainer
      cancelable={false}
      show={modalCustomerWelcomeVisible && !isLoading}
    >
      <div className="Flex Column Modal-customer-welcome-container">
        <img
          alt=""
          className="Modal-login-user-subscription-bubbles-image"
          src={syncIcon}
        />

        <h1 className="Modal-login-user-subscription-title">
          Welcome back!
        </h1>

        {renderContent()}

        <button
          ref={buttonRef}
          aria-label="Got it"
          className="Modal-customer-welcome-got-it"
          onClick={onClickGotIt}
          type="button"
        >
          <span className="Modal-customer-got-it-text">Got it</span>
        </button>
      </div>
    </ModalContainer>
  );
};

export default ModalCustomerWelcome;
